/**
 * ConfirmDialog — themed replacement for window.confirm().
 * Feb 2026 · Iter89 · used by the Portfolio batch archive / delete /
 * single-delete flows so the confirm prompt matches the design system.
 *
 * Iter90 · Feb 2026 · optional `select` config renders a labeled
 * dropdown inside the dialog. Confirm resolves to
 * `{ confirmed: true, values: {…} }` when a `select` is defined,
 * or a plain boolean otherwise (backwards-compatible).
 */
import { useCallback, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const _empty = {};

export function useConfirmDialog() {
  const [state, setState] = useState({
    open: false, title: "", body: "",
    confirmText: "Confirm", cancelText: "Cancel", danger: false,
    select: null,   // { name, label, options:[{value,label}], defaultValue }
  });
  const [values, setValues] = useState(_empty);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setValues(opts?.select?.defaultValue
        ? { [opts.select.name]: opts.select.defaultValue }
        : _empty);
      setState({
        open: true,
        title: opts?.title || "Please confirm",
        body: opts?.body || "",
        confirmText: opts?.confirmText || "Confirm",
        cancelText: opts?.cancelText || "Cancel",
        danger: !!opts?.danger,
        testid: opts?.testid || "confirm-dialog",
        select: opts?.select || null,
      });
    });
  }, []);

  const handle = (accepted) => {
    setState((s) => ({ ...s, open: false }));
    const r = resolverRef.current;
    resolverRef.current = null;
    if (state.select) r?.(accepted ? { confirmed: true, values } : false);
    else              r?.(!!accepted);
  };

  const Portal = () => (
    <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) handle(false); }}>
      <AlertDialogContent data-testid={state.testid}>
        <AlertDialogHeader>
          <AlertDialogTitle data-testid={`${state.testid}-title`}>{state.title}</AlertDialogTitle>
          {state.body && (
            <AlertDialogDescription data-testid={`${state.testid}-body`}>
              {state.body}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {state.select && (
          <div className="pt-1" data-testid={`${state.testid}-select-wrap`}>
            <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-mono mb-1">
              {state.select.label || state.select.name}
            </label>
            <select
              value={values[state.select.name] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [state.select.name]: e.target.value }))}
              data-testid={`${state.testid}-select`}
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white focus:border-brand-blue focus:outline-none"
            >
              {(state.select.options || []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => handle(false)}
            data-testid={`${state.testid}-cancel`}
          >
            {state.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => handle(true)}
            data-testid={`${state.testid}-confirm`}
            className={state.danger ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
          >
            {state.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, Portal };
}
